import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MUSIC_EVENTS } from 'Sp/Angular/Events/Client/Gallery';
import { getWatermarkCanvasDimensions } from 'ts/common/components/slideshows/slideshowWatermarkUtils';
import { getBaseUrl } from './useClientApiFetch';
import useClientEventApiFetch from './useClientEventApiFetch';
import useEvent from './useEvent';

export interface ISpAuthorizationToken {
    getAuthorizationTokenForEvent: (
        forceRefresh?: boolean,
        request?: { albumId?: number | string }
    ) => Promise<string>;
}

export interface IUseClientSlideshowParams {
    /**
     * The photo id representing the slideshow to fetch (`photo/{photoId}`). Pass null to skip
     * fetching entirely — callers that only *might* have a slideshow (e.g. a cover that could be
     * either a photo or a slideshow) still have to call this hook unconditionally, so null lets
     * them opt out of the request without violating the rules of hooks.
     */
    photoId: Nullable<number>;
    /** Album context for the authorization token request, if any. */
    albumId?: number | string | null;
    spAuthorizationToken: ISpAuthorizationToken;
    spEventBus: SpAngularJs.ISpEventBus;
}

/**
 * Default slideshow audio volume (0..1), matching `spMusic`'s own `$ctrl.volumeLevel` default —
 * kept in sync until the first `MUSIC_EVENTS.VOLUME` event arrives from the shared gallery
 * volume control.
 */
export const DEFAULT_GALLERY_MUSIC_VOLUME = 0.5;

/**
 * Shared plumbing for rendering an authenticated client-gallery slideshow: fetches the
 * slideshow payload (with 403 → forced-token-refresh retry), loads the brand watermark
 * as an object URL, and ducks gallery background music while this slideshow's own music
 * is sounding. Consumed by both `ClientSlideshowViewer` (lightbox) and
 * `CoverSlideshowBackground` (intro page background) so the two surfaces share one
 * implementation of this plumbing.
 */
const useClientSlideshow = ({
    photoId,
    albumId,
    spAuthorizationToken,
    spEventBus
}: IUseClientSlideshowParams) => {
    const { brandId } = useEvent();
    const { response, loading, performFetch } = useClientEventApiFetch<SpApi.IEventSlideshow>(
        `photo/${photoId}`,
        { defer: true }
    );

    useEffect(() => {
        if (photoId === null || photoId === undefined) {
            return;
        }

        let cancelled = false;
        const tokenRequest =
            albumId !== null && albumId !== undefined && albumId !== '' && albumId !== 'all'
                ? { albumId }
                : undefined;

        (async () => {
            const token = await spAuthorizationToken.getAuthorizationTokenForEvent(
                false,
                tokenRequest
            );

            if (cancelled) {
                return;
            }

            const result = await performFetch({ headers: { Authorization: `Bearer ${token}` } });

            if (cancelled) {
                return;
            }

            if (result?.status === 403) {
                const freshToken = await spAuthorizationToken.getAuthorizationTokenForEvent(
                    true,
                    tokenRequest
                );

                if (!cancelled) {
                    performFetch({ headers: { Authorization: `Bearer ${freshToken}` } });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [photoId, albumId, spAuthorizationToken, performFetch]);

    const watermarkPath = useMemo(() => {
        const wid = response?.brandWatermarkId;

        if (!wid || !brandId) {
            return undefined;
        }

        const { width, height } = getWatermarkCanvasDimensions(response?.aspectRatio);

        return `/brand/${brandId}/watermark/${wid}?format=png&width=${width}&height=${height}`;
    }, [response?.brandWatermarkId, response?.aspectRatio, brandId]);

    const [watermarkUrl, setWatermarkUrl] = useState<string | undefined>();
    const [isWatermarkReady, setIsWatermarkReady] = useState(false);

    useEffect(() => {
        if (!watermarkPath) {
            setWatermarkUrl(undefined);
            setIsWatermarkReady(true);
            return;
        }

        setIsWatermarkReady(false);
        setWatermarkUrl(undefined);

        let cancelled = false;
        let objectUrl: string | undefined;

        (async () => {
            try {
                const tokenRequest =
                    albumId !== null && albumId !== undefined && albumId !== '' && albumId !== 'all'
                        ? { albumId }
                        : undefined;
                const token = await spAuthorizationToken.getAuthorizationTokenForEvent(
                    false,
                    tokenRequest
                );
                const res = await fetch(getBaseUrl() + watermarkPath, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!res.ok) {
                    if (!cancelled) {
                        setWatermarkUrl(undefined);
                        setIsWatermarkReady(true);
                    }

                    return;
                }

                const blob = await res.blob();

                if (!cancelled) {
                    objectUrl = URL.createObjectURL(blob);
                    setWatermarkUrl(objectUrl);
                    setIsWatermarkReady(true);
                }
            } catch {
                if (!cancelled) {
                    setWatermarkUrl(undefined);
                    setIsWatermarkReady(true);
                }
            }
        })();

        return () => {
            cancelled = true;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [watermarkPath, albumId, spAuthorizationToken]);

    // Duck gallery background music while this slideshow's own music is claiming exclusive
    // playback; released (but not resumed — see MUSIC_EVENTS) when the slideshow stops or
    // the consumer unmounts.
    const isAudioActiveRef = useRef(false);

    const handleAudioActiveChange = useCallback(
        (isActive: boolean) => {
            isAudioActiveRef.current = isActive;
            spEventBus.emit(isActive ? MUSIC_EVENTS.MUTE : MUSIC_EVENTS.UNMUTE);
        },
        [spEventBus]
    );

    useEffect(() => {
        return () => {
            spEventBus.emit(MUSIC_EVENTS.UNMUTE);
        };
    }, [spEventBus]);

    // Mirrors the shared gallery volume control (`<sp-music>`'s slider) so the slideshow's own
    // audio stays at the same level the visitor already chose for the gallery song — one control
    // governs whichever audio is currently sounding. Starts at the same default as spMusic's own
    // `$ctrl.volumeLevel` and is kept current by every VOLUME emit (on user change, and again
    // whenever a slideshow takes over audio, so mount order can never leave this stale).
    const [volume, setVolume] = useState(DEFAULT_GALLERY_MUSIC_VOLUME);

    useEffect(() => {
        return spEventBus.on<number>(MUSIC_EVENTS.VOLUME, (level) => {
            setVolume(level);
        });
    }, [spEventBus]);

    // The other half of that shared control: its mute state. Starts muted because nothing is
    // sounding until either the visitor turns audio on or gallery music hands its own sounding
    // over (spMusic broadcasts MUTED at both moments, and again whenever a slideshow takes over
    // audio, so mount order can never leave this stale).
    //
    // Only consumers whose slideshow has no play control of its own — `CoverSlideshowBackground`
    // — should honor this: it keeps that slideshow's song running on the slideshow's timeline
    // and merely silences it, so unmuting resumes in sync instead of restarting. A slideshow the
    // visitor explicitly pressed play on owns its own audio and ignores this.
    const [isMuted, setIsMuted] = useState(true);

    useEffect(() => {
        return spEventBus.on<boolean>(MUSIC_EVENTS.MUTED, (muted) => {
            setIsMuted(muted);
        });
    }, [spEventBus]);

    return {
        response,
        loading,
        watermarkUrl,
        isWatermarkReady,
        handleAudioActiveChange,
        volume,
        isMuted
    };
};

export default useClientSlideshow;
